import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { PageHeader } from "@/components/common/DashboardBits";
import PincodeInput from "@/components/common/PincodeInput";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Save, Loader2 } from "lucide-react";

export default function BankProfilePage() {
  const { user } = useAuth();
  const { t } = useLang();
  const [bank, setBank] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user?.entity_id) return;
    api.get(`/banks/${user.entity_id}`).then((r) => setBank(r.data));
  }, [user?.entity_id]);

  if (!bank) return <p className="text-slate-500">Loading…</p>;

  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const payload = (({ name, branch, registered_person, contact_email, contact_phone, portal_link, address, location, city, pincode }) =>
        ({ name, branch, registered_person, contact_email, contact_phone, portal_link, address, location, city, pincode }))(bank);
      const r = await api.put(`/banks/${user.entity_id}`, payload);
      setBank(r.data);
      toast.success("Profile updated");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader title={t("profile")} subtitle="Bank / NBFC profile" />
      <Card className="border border-slate-200 max-w-3xl">
        <CardContent className="p-6">
          <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label>Bank Name</Label><Input required value={bank.name} onChange={(e) => setBank({ ...bank, name: e.target.value })} /></div>
            <div><Label>Branch</Label><Input required value={bank.branch} onChange={(e) => setBank({ ...bank, branch: e.target.value })} /></div>
            <PincodeInput
              value={bank.pincode || ""}
              onChange={(p) => setBank({ ...bank, pincode: p })}
              onResolved={(r) => {
                if (r.isRajasthan) setBank({ ...bank, pincode: r.pincode, city: r.city, location: r.district });
              }}
              testId="bank-profile-pincode"
              label="Pincode"
            />
            <div><Label>City</Label><Input value={bank.city || ""} readOnly className="bg-slate-50" /></div>
            <div><Label>District</Label><Input value={bank.location || ""} readOnly className="bg-slate-50" /></div>
            <div><Label>Registered Person</Label><Input value={bank.registered_person || ""} onChange={(e) => setBank({ ...bank, registered_person: e.target.value })} /></div>
            <div><Label>Email</Label><Input type="email" required value={bank.contact_email} onChange={(e) => setBank({ ...bank, contact_email: e.target.value })} /></div>
            <div><Label>Phone</Label><Input value={bank.contact_phone} onChange={(e) => setBank({ ...bank, contact_phone: e.target.value })} /></div>
            <div className="md:col-span-2"><Label>Address</Label><Textarea value={bank.address || ""} onChange={(e) => setBank({ ...bank, address: e.target.value })} /></div>
            <div className="md:col-span-2"><Label>Valuation Portal Link</Label><Input value={bank.portal_link || ""} onChange={(e) => setBank({ ...bank, portal_link: e.target.value })} /></div>
            <div className="md:col-span-2">
              <Button type="submit" disabled={busy} className="bg-slate-950 hover:bg-slate-800" data-testid="bank-profile-save">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> {t("save")}</>}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
